/**
 * Haptics.
 *
 * `navigator.vibrate` is unavailable on iOS and gated on user activation elsewhere, so this
 * is treated strictly as an enhancement: every call is guarded, failures are swallowed, and
 * repeated patterns are rate limited so an animation can never buzz continuously.
 */

export type HapticPattern = 'remove' | 'blocked' | 'heart-lost' | 'complete';

const PATTERNS: Record<HapticPattern, number | number[]> = {
  remove: 12,
  blocked: [18, 40, 18],
  'heart-lost': [30, 60, 60],
  complete: [20, 50, 20, 50, 60],
};

const MINIMUM_INTERVAL_MS = 60;

class Haptics {
  private enabled = true;
  private userHasInteracted = false;
  private lastVibrationAt = 0;

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /** Called from the first real user gesture. */
  markUserInteraction(): void {
    this.userHasInteracted = true;
  }

  get supported(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
  }

  vibrate(pattern: HapticPattern): void {
    if (!this.enabled || !this.userHasInteracted || !this.supported) return;

    const now = Date.now();
    if (now - this.lastVibrationAt < MINIMUM_INTERVAL_MS) return;
    this.lastVibrationAt = now;

    try {
      navigator.vibrate(PATTERNS[pattern]);
    } catch {
      // Some browsers expose the API but refuse to use it; there is nothing to recover.
    }
  }
}

export const haptics = new Haptics();
